/**
 * Modal para importar grupo de leads
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box
} from '@mui/material';
import { GroupAddOutlined } from '@mui/icons-material';

interface ImportGroupModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (groupName: string) => void;
  leadsCount: number;
  userName?: string;
  city?: string;
  neighborhood?: string;
  sector?: string;
}

const ImportGroupModal: React.FC<ImportGroupModalProps> = ({
  open,
  onClose,
  onImport,
  leadsCount,
  userName,
  city,
  neighborhood,
  sector
}) => {
  const [groupName, setGroupName] = useState('');

  // Gerar nome automático quando o modal abrir
  useEffect(() => {
    if (open) {
      const autoName = generateAutoGroupName(userName, city, neighborhood, sector);
      setGroupName(autoName);
    }
  }, [open, userName, city, neighborhood, sector]);

  /**
   * Gera nome automático do grupo
   * Formato: "PrimeiroNome, Bairro, Cidade - Setor"
   */
  const generateAutoGroupName = (
    userName?: string,
    city?: string,
    neighborhood?: string,
    sector?: string
  ): string => {
    const parts: string[] = [];

    // Primeiro nome do usuário
    if (userName) {
      const firstName = userName.split(' ')[0];
      parts.push(firstName);
    }

    // Bairro
    if (neighborhood) {
      parts.push(neighborhood);
    }

    // Cidade
    if (city) {
      parts.push(city);
    }

    // Setor
    let sectorPart = '';
    if (sector) {
      sectorPart = ` - ${sector}`;
    }

    // Combinar: "Nome, Bairro, Cidade - Setor"
    const baseName = parts.join(', ');
    return baseName + sectorPart;
  };

  const handleImport = () => {
    if (groupName.trim()) {
      onImport(groupName.trim());
      setGroupName('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 107, 53, 0.2)',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid rgba(255, 107, 53, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupAddOutlined sx={{ color: '#FF6B35' }} />
          Importar Grupo de Leads
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
          Você está prestes a importar <strong style={{ color: '#FF6B35' }}>{leadsCount} leads</strong> da busca atual.
          Dê um nome para este grupo para facilitar a identificação.
        </Typography>

        <TextField
          fullWidth
          label="Nome do Grupo"
          placeholder="Ex: Varejo - Zona Sul - Jan/2026"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 107, 53, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#FF6B35',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-focused': {
                color: '#FF6B35',
              },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 107, 53, 0.1)' }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleImport}
          disabled={!groupName.trim()}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A5B 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FF8A5B 0%, #FF6B35 100%)',
            },
            '&:disabled': {
              background: 'rgba(255, 107, 53, 0.3)',
              color: 'rgba(255, 255, 255, 0.3)',
            }
          }}
        >
          Importar Grupo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportGroupModal;
